import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, InputBase, Typography } from '@mui/material';
import { tokens } from '../../../theme/tokens';
import type { CameraWithPricing } from '../../../utils/productDetailHelpers';
import { categorizeSpecKeys, formatSpecLabel, formatSpecValue, getSpecValue, validSpecKeys } from '../../../utils/specFormatting';
import { stripHtml } from '../../../utils/text';
import { computeSpecIssues, featureRestatesSpec, specIssueKind, SPEC_ISSUE_PHRASES } from '../specIssues';
import type { SpecIssueKind } from '../specIssues';
import { FieldSourceBadge } from '../FieldSourceBadge';
import { DetailSectionHeader, Dot, ExternalGlyph, InfoBox, OutlineButton, TextLink, WarnBanner } from './primitives';

// ─── Specifications ──────────────────────────────────────────────────────────

export function DealerSpecsTab({ product, onCopied }: { product: CameraWithPricing; onCopied: (count: number) => void }) {
  const [query, setQuery] = useState('');
  const [flagOnly, setFlagOnly] = useState(false);
  const specs = product.specifications ?? {};

  const model = useMemo(() => {
    const keys = validSpecKeys(specs);
    const categorized = categorizeSpecKeys(keys);
    const flags = computeSpecIssues(categorized, (k) => getSpecValue(specs, k), {
      weight: product.dimensions?.weight,
      weightUnit: product.dimensions?.weightUnit,
    });
    const groups = Array.from(categorized.entries()).map(([name, ks]) => ({
      name,
      rows: ks
        .map((key) => {
          const raw = getSpecValue(specs, key);
          return raw ? { key, label: formatSpecLabel(key), value: formatSpecValue(key, raw), flag: flags.get(key) || null } : null;
        })
        .filter((r): r is { key: string; label: string; value: string; flag: string | null } => !!r),
    }));
    const kindOrder: SpecIssueKind[] = ['unmapped-key', 'missing-unit', 'swapped-label', 'cross-field'];
    const fired = new Set(Array.from(flags.values()).map((m) => specIssueKind(m)));
    return { groups, total: keys.length, flags: flags.size, phrases: kindOrder.filter((k) => fired.has(k)).map((k) => SPEC_ISSUE_PHRASES[k]) };
  }, [specs, product.dimensions]);

  const q = query.trim().toLowerCase();
  const shownGroups = model.groups
    .map((g) => ({ ...g, all: g.rows, rows: g.rows.filter((r) => (!flagOnly || r.flag) && (!q || `${r.label} ${r.value}`.toLowerCase().includes(q))) }))
    .filter((g) => g.rows.length > 0);
  const shown = shownGroups.reduce((n, g) => n + g.rows.length, 0);

  const copyAll = () => {
    const text = model.groups.map((g) => `${g.name}\n${g.rows.map((r) => `${r.label}: ${r.value}`).join('\n')}`).join('\n\n');
    void navigator.clipboard?.writeText(text);
    onCopied(model.total);
  };

  const joinPhrases = (p: string[]) => (p.length > 1 ? `${p.slice(0, -1).join(', ')} and ${p[p.length - 1]}` : p[0] ?? '');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '22px', p: { xs: '16px', sm: '22px' } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Typography component="span" sx={{ fontSize: '15px', fontWeight: 600 }}>Specifications</Typography>
            <FieldSourceBadge product={product} field="specs" />
          </Box>
          <Typography component="span" sx={{ fontSize: '12.5px', color: tokens.ink.muted }}>
            {model.total} specifications{q || flagOnly ? ` · ${shown} shown` : ''}
          </Typography>
        </Box>
        <Box sx={{ flex: '0 1 320px', display: 'flex', alignItems: 'center', gap: '8px', height: 36, px: '11px', border: '1px solid', borderColor: tokens.line.default, borderRadius: '9px', bgcolor: tokens.surface.card, minWidth: 180 }}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke={tokens.ink.faint} strokeWidth="1.6" aria-hidden><circle cx="7" cy="7" r="4.5" /><path d="M10.5 10.5 14 14" strokeLinecap="round" /></svg>
          <InputBase value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search specifications" inputProps={{ 'aria-label': 'Search specifications' }} sx={{ flex: '1 1 auto', minWidth: 0, fontSize: '13px' }} />
        </Box>
        {model.flags > 0 && (
          <ButtonBase
            onClick={() => setFlagOnly((v) => !v)}
            aria-pressed={flagOnly}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', height: 36, px: '12px', border: '1px solid', borderColor: flagOnly ? tokens.signal.caution.border : tokens.line.default, borderRadius: '9px', bgcolor: flagOnly ? tokens.signal.caution.tint : tokens.surface.card, fontSize: '12.5px', fontWeight: 600, color: tokens.signal.caution.ink }}
          >
            <Dot color={tokens.signal.caution.solid} />
            <span>{model.flags} to check</span>
          </ButtonBase>
        )}
        <OutlineButton height={36} onClick={copyAll} sx={{ color: tokens.ink.body, '&:hover': { bgcolor: tokens.surface.sunken } }}>Copy all</OutlineButton>
      </Box>

      {model.flags > 0 && (
        <InfoBox>
          {model.flags} of {model.total} specifications may be wrong{model.phrases.length ? `: ${joinPhrases(model.phrases)}` : ''}. Motzu is reviewing them; use Report issue if you spot more.
        </InfoBox>
      )}

      {shownGroups.map((g) => {
        const flagged = g.all.filter((r) => r.flag).length;
        return (
          <Box key={g.name} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', pb: '8px', borderBottom: '1px solid', borderColor: tokens.line.default }}>
              <Typography component="span" sx={{ fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{g.name}</Typography>
              <Typography component="span" sx={{ fontSize: '12px', color: tokens.ink.faint }}>{g.rows.length}</Typography>
              <Box sx={{ flex: '1 1 auto' }} />
              {flagged > 0 && (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: tokens.signal.caution.ink }}>
                  <Dot color={tokens.signal.caution.solid} size={5} />{flagged} to check
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(min(100%, 380px), 1fr))' }, columnGap: '36px' }}>
              {g.rows.map((r) => (
                <Box key={r.key} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,2fr) minmax(0,3fr)', sm: 'minmax(0,170px) minmax(0,1fr)' }, gap: '16px', py: '10px', borderBottom: '1px solid', borderColor: tokens.line.hairline }}>
                  <Box component="span" sx={{ display: 'flex', alignItems: 'baseline', gap: '7px', fontSize: '12.5px', lineHeight: 1.5, color: tokens.ink.muted }}>
                    {r.flag && <Dot color={tokens.signal.caution.solid} size={5} sx={{ transform: 'translateY(-2px)' }} />}
                    <span>{r.label}</span>
                  </Box>
                  <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
                    <Typography component="span" sx={{ fontSize: '13.5px', lineHeight: 1.5, color: r.flag ? tokens.signal.caution.ink : tokens.ink.primary, wordBreak: 'break-word' }}>{r.value}</Typography>
                    {r.flag && <Typography component="span" sx={{ fontSize: '12px', lineHeight: 1.45, color: tokens.signal.caution.ink }}>{r.flag}</Typography>}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        );
      })}
      {shownGroups.length === 0 && (
        <Typography sx={{ py: '8px', fontSize: '13px', color: tokens.ink.faint }}>
          {model.total === 0 ? 'No specifications for this product.' : `No specifications match “${query}”.`}
        </Typography>
      )}
    </Box>
  );
}

// ─── Details ─────────────────────────────────────────────────────────────────

const FEATURES_COLLAPSED = 8;

const sourceOf = (url: string): string => {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '');
    if (/youtube|youtu\.be/.test(host)) return 'YouTube';
    if (/vimeo/.test(host)) return 'Vimeo';
    return host;
  } catch {
    return '';
  }
};

export function dealerDetailsCount(product: CameraWithPricing): number {
  return (
    (product.features?.length ?? 0) +
    (product.highlights?.length ?? 0) +
    (product.featureSections?.length ?? 0) +
    (product.inTheBox?.length ?? 0) +
    (product.documentation?.length ?? 0) +
    (product.videos?.length ?? 0)
  );
}

export function DealerDetailsTab({ product }: { product: CameraWithPricing }) {
  const [featuresAll, setFeaturesAll] = useState(false);
  const full = product.description ? stripHtml(product.description).trim() : '';
  const features = product.features ?? [];
  const highlights = product.highlights ?? [];
  const sections = product.featureSections ?? [];
  const box = product.inTheBox ?? [];
  const docs = product.documentation ?? [];
  const videos = product.videos ?? [];

  const { featureFlags, highlightFlags, duplicates } = useMemo(() => {
    const values = validSpecKeys(product.specifications)
      .map((k) => { const raw = getSpecValue(product.specifications, k); return raw ? formatSpecValue(k, raw) : null; })
      .filter((v): v is string => !!v);
    const f = features.map((t) => featureRestatesSpec(t, values));
    const h = highlights.map((t) => featureRestatesSpec(t, values));
    return { featureFlags: f, highlightFlags: h, duplicates: f.filter(Boolean).length + h.filter(Boolean).length };
  }, [features, highlights, product.specifications]);

  const shownFeatures = featuresAll ? features : features.slice(0, FEATURES_COLLAPSED);
  const label = (text: string) => (
    <Typography component="span" sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.faint }}>{text}</Typography>
  );
  const repeat = <Typography component="span" sx={{ fontSize: '11.5px', lineHeight: 1.45, color: tokens.signal.caution.ink }}>Repeats a specification value verbatim.</Typography>;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '28px', p: { xs: '16px', sm: '22px' } }}>
      <Box sx={{ flex: '1 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '28px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
          <DetailSectionHeader title="Description" note={<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><FieldSourceBadge product={product} field={full ? 'description' : 'shortDescription'} />Syncs to your storefront</Box>} />
          {product.shortDescription && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {label('Short description')}
              <Typography sx={{ fontSize: '14px', fontWeight: 500, lineHeight: 1.55 }}>{product.shortDescription}</Typography>
            </Box>
          )}
          {full ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {label('Description')}
              <Typography sx={{ fontSize: '13.5px', lineHeight: 1.65, color: tokens.ink.bodyAlt, maxWidth: '68ch', whiteSpace: 'pre-line' }}>{full}</Typography>
            </Box>
          ) : !product.shortDescription ? (
            <Typography sx={{ fontSize: '12.5px', color: tokens.ink.faint }}>No description from {product.brand}.</Typography>
          ) : null}
        </Box>

        {highlights.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
            <DetailSectionHeader title="Key highlights" count={highlights.length} />
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {highlights.map((h, i) => (
                <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: '2px', py: '7px', borderBottom: '1px solid', borderColor: tokens.surface.sunken }}>
                  <Typography component="span" sx={{ fontSize: '13.5px', lineHeight: 1.55 }}>{h}</Typography>
                  {highlightFlags[i] && repeat}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {features.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
            <DetailSectionHeader title="Features" count={features.length} />
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {shownFeatures.map((f, i) => (
                <Box key={i} sx={{ display: 'grid', gridTemplateColumns: { xs: 'auto minmax(0,1fr)', sm: '20px minmax(0,1fr)' }, gap: '10px', py: '7px', borderBottom: '1px solid', borderColor: tokens.surface.sunken }}>
                  <Typography component="span" sx={{ fontSize: '12px', color: tokens.ink.faint, textAlign: 'right' }}>{i + 1}</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                    <Typography component="span" sx={{ fontSize: '13.5px', lineHeight: 1.55 }}>{f}</Typography>
                    {featureFlags[i] && repeat}
                  </Box>
                </Box>
              ))}
            </Box>
            {features.length > FEATURES_COLLAPSED && (
              <TextLink onClick={() => setFeaturesAll((v) => !v)}>{featuresAll ? 'Show fewer' : `Show all ${features.length} features`}</TextLink>
            )}
          </Box>
        )}

        {duplicates > 0 && (
          <WarnBanner>
            {duplicates} of {features.length + highlights.length} highlight and feature entr{features.length + highlights.length === 1 ? 'y restates' : 'ies restate'} a specification value word for word. This copy is what sells the product on your storefront; spec tables already carry the numbers.
          </WarnBanner>
        )}

        {sections.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
            <DetailSectionHeader title="Feature sections" count={sections.length} />
            {sections.map((s, i) => (
              <Box key={i} sx={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexDirection: { xs: 'column', sm: s.imageUrl ? 'row' : 'column' } }}>
                {s.imageUrl && (
                  <Box component="img" src={s.imageUrl} alt={s.title} onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }} sx={{ width: { xs: '100%', sm: 160 }, borderRadius: '10px', flexShrink: 0, objectFit: 'cover' }} />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '13.5px', fontWeight: 600, mb: '3px' }}>{s.title}</Typography>
                  <Typography sx={{ fontSize: '13px', lineHeight: 1.6, color: tokens.ink.bodyAlt, whiteSpace: 'pre-line' }}>{stripHtml(s.body)}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {box.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
            <DetailSectionHeader title="In the box" count={box.length} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(220px, 1fr))' }, columnGap: '24px' }}>
              {box.map((b, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', py: '7px', borderBottom: '1px solid', borderColor: tokens.surface.sunken, fontSize: '13.5px', lineHeight: 1.45 }}>
                  <Dot color={tokens.ink.disabled} size={5} sx={{ transform: 'translateY(-2px)' }} />
                  <span>
                    {b.name}
                    {b.quantity != null && b.quantity > 1 && <Box component="span" sx={{ fontWeight: 600, color: tokens.ink.muted }}> ×{b.quantity}</Box>}
                    {b.sku && <Box component="span" sx={{ ml: '6px', fontSize: '11.5px', color: tokens.ink.faint }}>{b.sku}</Box>}
                  </span>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ flex: '1 1 280px', maxWidth: { md: 420 }, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '28px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <DetailSectionHeader title="Documentation" count={docs.length} />
          {docs.map((d, i) => (
            <Box key={i} component="a" href={d.url} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: '12px', py: '11px', borderBottom: '1px solid', borderColor: tokens.line.hairline, color: tokens.ink.primary, textDecoration: 'none', '&:hover': { bgcolor: tokens.surface.sunken } }}>
              <Box component="span" sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Typography component="span" sx={{ fontSize: '13.5px', fontWeight: 500 }}>{d.title}</Typography>
                <Typography component="span" sx={{ fontSize: '12px', color: tokens.ink.faint, textTransform: 'capitalize' }}>{String(d.type || 'document').replace(/-/g, ' ')}</Typography>
              </Box>
              <Box component="span" sx={{ color: tokens.ink.faint, display: 'inline-flex' }}><ExternalGlyph size={14} /></Box>
            </Box>
          ))}
          {docs.length === 0 && <Typography sx={{ py: '11px', fontSize: '12.5px', color: tokens.ink.faint }}>No documentation from {product.brand}.</Typography>}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <DetailSectionHeader title="Videos" count={videos.length} />
          {videos.map((v, i) => (
            <Box key={i} component="a" href={v.url} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: '12px', py: '10px', borderBottom: '1px solid', borderColor: tokens.line.hairline, color: tokens.ink.primary, textDecoration: 'none' }}>
              <Box component="span" sx={{ position: 'relative', flex: '0 0 88px', height: 50, borderRadius: '6px', bgcolor: tokens.surface.header, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {v.thumbnailUrl && (
                  <Box component="img" src={v.thumbnailUrl} alt="" onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
                <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden style={{ position: 'relative' }}><path d="M5 3.5v9l7.5-4.5z" fill={tokens.ink.inverse} /></svg>
              </Box>
              <Box component="span" sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Typography component="span" sx={{ fontSize: '13.5px', fontWeight: 500, lineHeight: 1.4 }}>{v.title || 'Watch video'}</Typography>
                <Typography component="span" sx={{ fontSize: '12px', color: tokens.ink.faint }}>{[sourceOf(v.url), v.type].filter(Boolean).join(' · ')}</Typography>
              </Box>
            </Box>
          ))}
          {videos.length === 0 && <Typography sx={{ py: '11px', fontSize: '12.5px', color: tokens.ink.faint }}>No videos from {product.brand}.</Typography>}
        </Box>
      </Box>
    </Box>
  );
}
